import React from 'react';
import { Link } from 'react-router-dom';
import { CHARACTERS } from '../lib/levels';
import { Sparkles, BookOpen, Trophy, Users } from 'lucide-react';

const Logo = () => (
  <div className="flex items-center gap-2" data-testid="brand-logo">
    <div className="w-10 h-10 rounded-2xl bg-navy flex items-center justify-center text-gold font-heading text-xl font-bold">O</div>
    <span className="font-heading text-2xl font-bold text-navy tracking-tight">OuiStuPaStu</span>
  </div>
);

const CharacterCard = ({ character }) => (
  <div className="bg-white rounded-3xl p-5 shadow-squishy border-2 border-cream flex flex-col items-center text-center animate-bounce-in">
    <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-teal mb-3">
      <img src={character.image} alt={character.name} className="w-full h-full object-cover" />
    </div>
    <h3 className="font-heading text-lg font-bold text-navy">{character.name}</h3>
    <p className="text-xs text-navy/60">{character.role}</p>
  </div>
);

export default function Landing() {
  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="px-4 md:px-8 py-5 flex items-center justify-between max-w-6xl mx-auto">
        <Logo />
        <div className="flex gap-2">
          <Link
            to="/login"
            className="px-5 py-2.5 rounded-full font-heading font-semibold text-navy hover:bg-white transition-colors duration-200"
            data-testid="header-login-link"
          >
            Log in
          </Link>
          <Link
            to="/register"
            className="px-5 py-2.5 rounded-full font-heading font-semibold bg-navy text-white hover:scale-105 active:scale-95 transition-transform duration-200 shadow-squishy"
            data-testid="header-signup-link"
          >
            Join Free
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="px-4 md:px-8 py-10 md:py-16 max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-teal/10 text-teal font-semibold text-sm mb-5">
              <Sparkles className="w-4 h-4" /> A family that learns together
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-navy leading-[1.05]">
              3 questions a day.<br />
              <span className="text-teal">A lifetime of curiosity.</span>
            </h1>
            <p className="mt-5 text-lg text-navy/70 max-w-lg">
              Join Ouistiti, Stupidopoulos, Stupidopoulou and Patapouf on a daily adventure through science,
              geography, history and more — designed for kids, parents and teachers.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/register"
                className="px-8 py-4 rounded-full font-heading font-bold text-navy bg-gold shadow-gold hover:scale-105 active:scale-95 transition-transform duration-200 text-lg"
                data-testid="hero-cta-signup"
              >
                Start Learning Free
              </Link>
              <Link
                to="/login"
                className="px-8 py-4 rounded-full font-heading font-bold text-navy bg-white border-2 border-navy hover:scale-105 active:scale-95 transition-transform duration-200 text-lg"
                data-testid="hero-cta-login"
              >
                I have an account
              </Link>
            </div>
            <p className="mt-4 text-sm text-navy/50">Demo accounts: parent@test.com · child@test.com · teacher@test.com · password: <span className="font-mono font-semibold">test123</span></p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {Object.values(CHARACTERS).map((c) => (
              <CharacterCard key={c.name} character={c} />
            ))}
          </div>
        </div>
      </section>

      {/* Feature Row */}
      <section className="px-4 md:px-8 py-10 max-w-6xl mx-auto">
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { icon: BookOpen, title: 'Daily 3-Question Quiz', desc: 'Bite-sized learning with instant feedback and level-ups.', color: 'text-teal' },
            { icon: Trophy, title: '7 Level Ranks', desc: 'From Explorer to Knowble ⭐ — every answer moves you forward.', color: 'text-gold' },
            { icon: Users, title: 'Three Roles, One App', desc: 'Kids play, parents track, teachers coach — all in one place.', color: 'text-navy' },
          ].map((f) => (
            <div key={f.title} className="bg-white rounded-3xl p-8 shadow-squishy border-2 border-cream">
              <f.icon className={`w-10 h-10 ${f.color} mb-4`} />
              <h3 className="font-heading text-xl font-bold text-navy mb-2">{f.title}</h3>
              <p className="text-navy/70">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="text-center py-10 text-navy/50 text-sm">
        © 2026 OuiStuPaStu · Learn boldly, together.
      </footer>
    </div>
  );
}
